import React, { useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import DataTable from "../DataTable";
import Alert from "../Alert";
import StudentPicker from "../StudentPicker";
import StaffPicker from "../StaffPicker";
import { listLeaves, createLeave, cancelLeave } from "../../api/attendanceApi";
import "../../pages/Users/UsersPage.css";

/** Real fixed reason list, direct port of the old app's own `<select id="leavereason">` — both student-leave-input.html and hr-leave-input.html share the exact same options. */
const LEAVE_REASONS = [
  "Accidental Issue",
  "Doctor Appointment",
  "Death Of Family Member",
  "Family Issue",
  "Natural Disaster",
  "Personal Issue",
  "Political",
  "Sick",
  "Sports",
  "Strike",
  "Traffic Jam",
];

function daysBetween(fromDate, toDate) {
  const dates = [];
  const cursor = new Date(fromDate + "T00:00:00");
  const end = new Date(toDate + "T00:00:00");
  while (cursor <= end) {
    dates.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, "0")}-${String(cursor.getDate()).padStart(2, "0")}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

/**
 * Shared leave entry UI for both students and staff — direct port of
 * digicampusbd's `student-leave-input`/`hr-leave-input`, both confirmed to
 * share this exact same real shape (find by Custom Student/Staff ID rather
 * than a raw internal id, a From/To Date **range** — the real save endpoint
 * creates one `Leave` row per real calendar day in that range, direct port
 * of `StudentLeaveService.getDaysBetweenDates` — and a fixed 11-option Leave
 * Reason dropdown, not free text). Found and fixed three real gaps here,
 * while sweeping the Attendance module (see MIGRATION.md): this page
 * previously required staff to already know the person's raw internal
 * `identificationId`/`staffId` (never shown anywhere in this app's own UI),
 * only ever created a single day's leave per save (no way to request a
 * multi-day leave in one action, matching a common real use case — sick
 * leave, family emergency), and let "Reason" be typed freely instead of the
 * real fixed list. The picker itself uses the already-established
 * `StudentPicker` component (cascading year→class→student, the same
 * "resolve a person without knowing their internal id" solution already
 * used throughout Fee Management) / a new sibling `StaffPicker`, rather than
 * literally reproducing the old app's own type-a-custom-id-then-search flow
 * — same real capability, no new backend needed either way. The day-range
 * expansion happens client-side (one `createLeave` call per day, same
 * already-existing single-day endpoint/duplicate-date conflict check) since
 * the backend's own per-day primitive was already correct and didn't need
 * changing to support this.
 */
export default function LeaveManager({ subjectType, title }) {
  const [person, setPerson] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [form, setForm] = useState({ fromDate: "", toDate: "", leaveReason: "", leaveFor: "" });

  const personId = subjectType === "STAFF" ? person && person.staffId : person && person.identificationId;

  const load = (id) => {
    if (!id) {
      setLeaves([]);
      return;
    }
    setLoading(true);
    const params = subjectType === "STAFF" ? { staffId: id } : { identificationId: id };
    listLeaves(params)
      .then(setLeaves)
      .catch(() => setError("Failed to load leave entries."))
      .finally(() => setLoading(false));
  };

  const handleSelect = (p) => {
    setPerson(p);
    setError("");
    setSuccess("");
    load(subjectType === "STAFF" ? p && p.staffId : p && p.identificationId);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.fromDate || !form.toDate || !form.leaveReason) {
      setError("From Date, To Date, and Leave Reason are required.");
      return;
    }
    if (form.toDate < form.fromDate) {
      setError("To Date can't be before From Date.");
      return;
    }
    const dates = daysBetween(form.fromDate, form.toDate);
    setSaving(true);
    try {
      // One real Leave row per real calendar day — direct port of the old app's own
      // server-side day expansion, done here against the same single-day endpoint.
      for (const leaveDate of dates) {
        // eslint-disable-next-line no-await-in-loop
        await createLeave({
          leaveDate,
          leaveReason: form.leaveReason,
          leaveFor: form.leaveFor,
          ...(subjectType === "STAFF" ? { staffId: Number(personId) } : { identificationId: Number(personId) }),
        });
      }
      setSuccess(`Leave entry created for ${dates.length} day(s).`);
      setForm({ fromDate: "", toDate: "", leaveReason: "", leaveFor: "" });
      load(personId);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to create leave entry.");
      load(personId); // some days in the range may have already saved before the failing one
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (leave) => {
    if (!window.confirm("Cancel this leave entry?")) return;
    setError("");
    try {
      await cancelLeave(leave.leaveId);
      setSuccess("Leave entry cancelled.");
      load(personId);
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Failed to cancel.");
    }
  };

  const columns = [
    { key: "leaveDate", label: "Date" },
    { key: "leaveReason", label: "Reason" },
    { key: "leaveFor", label: "Details" },
    { key: "leaveStatus", label: "Status", render: (row) => (row.leaveStatus === 1 ? "Active" : "Cancelled") },
  ];

  return (
    <MainLayout>
      <div className="page-header">
        <h1>{title}</h1>
      </div>

      <Alert type="error" onDismiss={() => setError("")}>
        {error}
      </Alert>
      <Alert type="success" onDismiss={() => setSuccess("")}>
        {success}
      </Alert>

      {subjectType === "STAFF" ? <StaffPicker onSelect={handleSelect} /> : <StudentPicker onSelect={handleSelect} />}

      {person && (
        <form onSubmit={handleSubmit} className="user-form" style={{ marginTop: 16, marginBottom: 24 }}>
          <label>From Date *</label>
          <input type="date" value={form.fromDate} onChange={(e) => setForm({ ...form, fromDate: e.target.value })} required />
          <label>To Date *</label>
          <input type="date" value={form.toDate} onChange={(e) => setForm({ ...form, toDate: e.target.value })} required />
          <label>Leave Reason *</label>
          <select value={form.leaveReason} onChange={(e) => setForm({ ...form, leaveReason: e.target.value })} required>
            <option value="" disabled>
              Select Reason
            </option>
            {LEAVE_REASONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
          <label>Leave For</label>
          <textarea value={form.leaveFor} onChange={(e) => setForm({ ...form, leaveFor: e.target.value })} />
          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={saving}>
              {saving ? "Saving..." : "+ Add Leave Entry"}
            </button>
          </div>
        </form>
      )}

      {person &&
        (loading ? (
          <p>Loading...</p>
        ) : (
          <DataTable
            columns={columns}
            rows={leaves}
            actions={(row) =>
              row.leaveStatus === 1 && (
                <button type="button" className="btn-link danger" onClick={() => handleCancel(row)}>
                  Cancel
                </button>
              )
            }
          />
        ))}
    </MainLayout>
  );
}
